import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import Persona, { ClientOptions } from 'persona';

import { CombinedState } from '../state-management/state/combined.state';
import { InstitutionState } from '../state-management/state/institution.state';
import { Step, ProgressState } from '../state-management/state/progress.state';
import { ApplicantsState } from '../state-management/state/applicants.state';
import { SessionState } from '../state-management/state/session.state';
import { ProgressActionTypes } from '../state-management/actions/progress.actions';
import { ApplicantsActionsTypes } from '../state-management/actions/applicants.actions';
import { ApplicantsEffects } from '../state-management/effects/applicants.effects';

@Component({
    selector: 'app-persona-id-collection',
    templateUrl: './persona-id-collection.component.html',
    styleUrls: ['./persona-id-collection.component.scss'],
    standalone: false
})
export class PersonaIdCollectionComponent implements OnInit, OnDestroy {

  // UI / template-bound
  useNewUiForBank = false;
  isPanelVisible = false;
  pageName = 'persona';
  isLoading = true;
  canContinue = true;

  // Applicant state
  currentApplicant = 0;
  currentApplicantID = '';
  formattedDate = '';
  private applicant: any;

  // Institution config
  private templateID = '';
  private environmentId = '';
  allowAutoFill = false;
  isPersonaEnabled = false;
  bankNumber = '';

  // Session
  private confirmationCode = '';

  // Persona lifecycle guards
  private personaClient: any = null;
  private personaInitialized = false;
  private personaCompleted = false;
  private personaClientToken = 0;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private store: Store<CombinedState>,
    private router: Router,
    private applicantsEffects: ApplicantsEffects
  ) {}

  // ─────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────
  ngOnInit(): void {

    /* Applicants */
    this.store.select('applicantsStoreReducer')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: ApplicantsState) => {
        this.currentApplicant = state.currentApplicantIndex;
        this.applicant = state.applicants[this.currentApplicant];
        this.currentApplicantID = this.applicant.id;

        const datePipe = new DatePipe('en-US');
        this.formattedDate = datePipe.transform(this.applicant.CipDate, 'dd/MM/yyyy') ?? '';

        this.tryInitPersona();
      });

    /* Institution */
    this.store.select('institutionStoreReducer')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: InstitutionState) => {
        this.useNewUiForBank = state.institution.useNewUiForBank;
        this.allowAutoFill = state.institution.allowAutoFill;
        this.templateID = state.institution.personaTemplateId;
        this.environmentId = state.institution.personaEnvironmentId;
        this.isPersonaEnabled = state.institution.enablePersonaIdScanning;
        this.bankNumber = state.institution.bankNumber;

        this.tryInitPersona();
      });

    /* Session */
    this.store.select('sessionStoreReducer')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: SessionState) => {
        this.confirmationCode = state.confirmationCode;
        this.tryInitPersona();
      });

    /* Progress */
    this.store.select('progressStoreReducer')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((progress: ProgressState) => {
        if (this.currentApplicant === 0 && progress.currentStep < Step.PersonaPrimaryApplicant) {
          this.store.dispatch({ type: ProgressActionTypes.SET_PROGRESS, payload: Step.PersonaPrimaryApplicant });
        } else if (this.currentApplicant === 1 && progress.currentStep < Step.PersonaSecondaryApplicant) {
          this.store.dispatch({ type: ProgressActionTypes.SET_PROGRESS, payload: Step.PersonaSecondaryApplicant });
        }
      });

    /* Save success → navigate */
    this.applicantsEffects.appSaveInfo$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.navigateNext());

    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.cleanupPersonaClient(false);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  private tryInitPersona(): void {
    if (this.isPersonaMarkedComplete()) {
      this.personaCompleted = true;
      return;
    }

    if (
      this.personaInitialized ||
      this.personaCompleted ||
      !this.templateID ||
      !this.environmentId ||
      !this.currentApplicantID
    ) {
      return;
    }

    const clientToken = ++this.personaClientToken;

    const options: ClientOptions = {
      templateId: this.templateID,
      environmentId: this.environmentId,
      referenceId: this.currentApplicantID,

      onReady: () => {
        if (!this.isActivePersonaClient(clientToken)) { return; }
        this.personaClient?.open();
      },

      onComplete: ({ inquiryId, status, fields }) => {
        if (!this.isActivePersonaClient(clientToken)) { return; }
        console.log(`Sending finished inquiry ${inquiryId} to backend`);

        const f = fields as any;
        if (
          !f['current-selfie']?.value ||
          !f['identification-number']?.value ||
          !f['issue-date']?.value
        ) {
          console.log('fail');
          this.cleanupPersonaClient(true);
          this.setPersonaMarkedComplete(false);
          this.saveApplicant(f, ApplicantIDCollectionStatus.Failure);
          this.router.navigate(['verification-error']);
          return;
        }

        this.personaCompleted = true;
        this.setPersonaMarkedComplete(true);
        this.cleanupPersonaClient(false);
        this.saveApplicant(f, ApplicantIDCollectionStatus.Success);
        this.canContinue = false;
      },

      onCancel: () => {
        if (!this.isActivePersonaClient(clientToken)) { return; }
        this.cleanupPersonaClient(true);
        this.setPersonaMarkedComplete(false);
        this.router.navigate(['personaconsent']);
      },

      onError: () => {

        if (!this.isActivePersonaClient(clientToken)) { return; }
        this.cleanupPersonaClient(true);
        this.setPersonaMarkedComplete(false);
        this.router.navigate(['verification-error']);
      }
    };

    try {
      this.personaClient = new Persona.Client(options);
      this.personaInitialized = true;
    } catch (err) {
      console.error('Persona client construction failed', err);
      this.personaClient = null;
      this.router.navigate(['verification-error']);
      return;
    }

  }

  private cleanupPersonaClient(resetInitialization: boolean): void {
    this.personaClientToken++;
    this.personaClient?.destroy();
    this.personaClient = null;
    if (resetInitialization) {
      this.personaInitialized = false;
    }
  }

  private isActivePersonaClient(token: number): boolean {
    return token === this.personaClientToken && this.personaClient !== null;
  }

  private getPersonaCompletionKey(): string {
    return `persona-completed:${this.currentApplicantID || 'unknown'}`;
  }

  private isPersonaMarkedComplete(): boolean {
    return sessionStorage.getItem(this.getPersonaCompletionKey()) === '1';
  }

  private setPersonaMarkedComplete(value: boolean): void {
    const key = this.getPersonaCompletionKey();
    if (value) {
      sessionStorage.setItem(key, '1');
      return;
    }
    sessionStorage.removeItem(key);
  }

  // ─────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────
  navigateNext(): void {
    this.router.navigate([
      this.currentApplicant > 0 ? 'personal-information' : 'products'
    ]);
  }

  // ─────────────────────────────────────────────
  // Save applicant
  // ─────────────────────────────────────────────
  private saveApplicant(fields: any, personaStatus: ApplicantIDCollectionStatus): void {

    this.applicant.city = fields['address-city']?.value;
    this.applicant.zipCode = fields['address-postal-code']?.value;
    this.applicant.address1 = fields['address-street-1']?.value;
    this.applicant.address2 = fields['address-street-2']?.value;
    this.applicant.state = fields['address-subdivision']?.value;

    if (this.currentApplicant === 1) {
      this.applicant.verifyEmailAddress = this.applicant.emailAddress;
    }

    this.applicant.taxIdentificationNumber = fields['social-security-number']?.value;
    this.applicant.dateOfBirth = fields['birthdate']?.value;
    this.applicant.phoneNumber = fields['phone-number']?.value?.slice(-10);
    this.applicant.firstName = fields['name-first']?.value;
    this.applicant.lastName = fields['name-last']?.value;
    this.applicant.middleInitial = fields['name-middle']?.value
      ? fields['name-middle'].value.charAt(0)
      : null;

    this.applicant.identificationType =
      fields['identification-class']?.value === 'dl' ? '5' : '8';

    this.applicant.identificationExpirationDate = fields['expiration-date']?.value;
    this.applicant.identificationState = fields['address-subdivision']?.value;
    this.applicant.identificationNumber = fields['identification-number']?.value;
    this.applicant.identificationIssueDate = fields['issue-date']?.value;

    this.applicant.CipDate = this.formattedDate;
    this.applicant.CipLevel = this.isPersonaEnabled ? 'K' : '';

    this.store.dispatch({
      type: ApplicantsActionsTypes.SAVE_APPLICANT,
      payload: {
        applicant: this.applicant,
        personaStatus
      }
    });

    this.store.dispatch({
      type: ApplicantsActionsTypes.UPDATE_CURRENT_APPLICANT,
      payload: this.applicant
    });
  }
}

export enum ApplicantIDCollectionStatus {
  Success = 0,
  Failure = 1,
  Timeout = 2
}
